import { createSlice, current } from '@reduxjs/toolkit';
import moment from 'moment';
import { convertToNumberIfPossible } from '../../utils/convertToNumberIfPossible';
import { isEmpty } from 'lodash';
import { SideFilterAttr } from '../../components/SideFilters/interface';
import { RootState } from '../../store/store';
import { convertMultiRoomToPaxCount } from '../../utils/convertMultiRoomToPaxCount';
import { SIDE_FILTER_KEYS } from '../../utils/constants';

const searchDateFormat = 'DD/MM/YYYY';
const params = new URL(window.location.href).searchParams;
interface initialStateInterface {
  searchParams: any;
  searchResults: any[];
  sideFilters: any;
  feature: string | null;
  mfeature: string | null;
  flightDestinationAirport: {
    value: string;
    location_id: string;
    name: string;
    // label: React.ReactNode;
  } | null;
  flightDepartureAirport: {
    value: string;
    location_id: string;
    name: string;
    // label: React.ReactNode;
  } | null;
  modalSearchParams: any;
  isReselectFlightBundleFetching: boolean;
  modalSideFilters: any;
  scrollToResults: boolean;
}

const initialState: initialStateInterface = {
  searchParams: {
    f: !params.get('f') ? null : params.get('f'),
    h: !params.get('h') ? null : params.get('h'),
    eTicket: !params.get('eTicket') ? null : params.get('eTicket'),
    dynamicPackageSearch: null,

    from: null,
    to: null,
    // Optional date range used only for events search (events / events+DP flow)
    eventFrom: !params.get('eventFrom') ? null : params.get('eventFrom'),
    eventTo: !params.get('eventTo') ? null : params.get('eventTo'),
    pax:
      params.get('pax') === '' || params.get('pax') === null
        ? null
        : params.get('pax')?.split(','),
    rooms:
      params.get('rooms') === '' || params.get('rooms') === null
        ? null
        : params.get('rooms'),
    adult:
      params.get('adult') === '' || params.get('adult') === null
        ? 2
        : params.get('adult'),
    child:
      params.get('child') === '' || params.get('child') === null
        ? 0
        : params.get('child'),
    infant:
      params.get('infant') === '' || params.get('infant') === null
        ? 0
        : params.get('infant'),
    oneWay:
      params.get('oneWay') === '' || params.get('oneWay') === null
        ? '0'
        : params.get('oneWay'),
    isDpSearch:
      params.get('isDpSearch') === '' || params.get('isDpSearch') === null
        ? '0'
        : params.get('isDpSearch'),
    prvmd:
      params.get('prvmd') === '' || params.get('prvmd') === null
        ? '0'
        : params.get('prvmd'),
    vacationType:
      params.get('vacationType') == '' || params.get('vacationType') === null
        ? null
        : params.get('vacationType'),
    destination:
      params.get('destination') === '' || params.get('destination') === null
        ? null
        : params.get('destination')?.includes(',')
        ? params
            ?.get('destination')
            ?.split(',')
            .map((entry) => Number(entry))
        : params.get('destination'),
    departure:
      params.get('departure') === '' || params.get('departure') === null
        ? null
        : params.get('departure')?.includes(',')
        ? params
            ?.get('departure')
            ?.split(',')
            .map((entry) => Number(entry))
        : params.get('departure'),
    destinationCity:
      params.get('destinationCity') === '' ||
      params.get('destinationCity') === null
        ? null
        : params.get('destinationCity'),
    extId:
      params.get('extId') === '' || params.get('extId') === null
        ? null
        : params.get('extId'),
    departureCity:
      params.get('departureCity') === '' || params.get('departureCity') === null
        ? null
        : params.get('departureCity'),
    directFlight:
      params.get('directFlight') === '' || params.get('directFlight') === '0'
        ? '0'
        : params.get('directFlight') ?? '0',
    boardBasis:
      params.get('boardBasis') === '' || params.get('boardBasis') === null
        ? null
        : params.get('boardBasis')?.split(','),
    multi_room_guests:
      params.get('multi_room_guests') === '' ||
      params.get('multi_room_guests') === null
        ? [{ adult: 2, child: 0, infant: 0 }]
        : JSON.parse(
            params.get('multi_room_guests') ??
              "[{'adult':2, child:0, infant:0}]"
          ),
    hotelStar:
      params.get('hotelStar') === '' || params.get('hotelStar') === null
        ? null
        : params.get('hotelStar')?.split(','),
    ext_type:
      params.get('ext_type') === '' || params.get('ext_type') === null
        ? null
        : params.get('ext_type'),
    extName:
      params.get('extName') === '' || params.get('extName') === null
        ? null
        : params.get('extName'),
  },
  searchResults: [],
  sideFilters: {},
  feature: null,
  mfeature: null,
  flightDestinationAirport: null,
  flightDepartureAirport: null,
  modalSearchParams: {},
  isReselectFlightBundleFetching: false,
  modalSideFilters: {},
  scrollToResults: false,
};

// ONLY POPULATE WITH SIDE FILTERS FROM URL
params.forEach((value, key) => {
  if (SIDE_FILTER_KEYS.has(key)) {
    if (!initialState.sideFilters.hasOwnProperty(key)) {
      initialState.sideFilters[key] = [];
    }
    // keep original behaviour of splitting and converting if possible
    initialState.sideFilters[key] = value
      .split(',')
      .map(convertToNumberIfPossible);
    return;
  }
  if (initialState.searchParams.hasOwnProperty(key)) {
    if (
      initialState.searchParams[key] === null ||
      initialState.searchParams[key] === undefined
    ) {
      initialState.searchParams[key] = convertToNumberIfPossible(value);
    }
  } else {
    // just hardcode these keys as not side filters (for agent and token)
    if (key === 'for' || key === 't') {
      return;
    }
    const unprefixedKey = key.replace(/^(f1_|f2_)/, '');
    if (
      Object.values(SideFilterAttr).includes(key as SideFilterAttr) ||
      (key !== unprefixedKey && Object.values(SideFilterAttr).includes(unprefixedKey as SideFilterAttr))
    ) {
      if (!initialState.sideFilters.hasOwnProperty(key)) {
        initialState.sideFilters[key] = [];
      }
      initialState.sideFilters[key] = value
        .split(',')
        .map(convertToNumberIfPossible);
    }
    // Unrecognized params are left alone — the URL sync must not delete legitimate search params
  }
});

export const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    updateModalSearchParams: (state, action) => {
      state.modalSearchParams = action.payload;
    },
    updateSideFiltersFunction: (state, action) => {
      const latestParams = new URLSearchParams(action.payload);

      latestParams.forEach((value, key) => {
        // let t1 = state.sideFilters[key];
        if (SIDE_FILTER_KEYS.has(key)) {
          if (!state.sideFilters.hasOwnProperty(key)) {
            state.sideFilters[key] = [];
          }
          // keep original behaviour of splitting and converting if possible
          state.sideFilters[key] = value
            ?.split(',')
            .map(convertToNumberIfPossible);
          return;
        }
        // let t = state.searchParams[key];
        if (state.searchParams.hasOwnProperty(key)) {
          // if (
          //   state.searchParams[key] === null ||
          //   state.searchParams[key] === undefined
          // ) {
          state.searchParams[key] = convertToNumberIfPossible(value);
          //   }
          // } else {
          //   if (!state.sideFilters.hasOwnProperty(key)) {
          //     state.sideFilters[key] = [];
          //   }

          // just hardcode these keys as not side filters (for agent and token)
          if (key === 'for' || key === 't') {
            return;
          }
          if (Object.values(SideFilterAttr).includes(key as SideFilterAttr)) {
            state.sideFilters[key] = value
              ?.split(',')
              .map(convertToNumberIfPossible);
          }
        } else {
          const unprefixedKey = key.replace(/^(f1_|f2_)/, '');
          if (
            key !== unprefixedKey &&
            Object.values(SideFilterAttr).includes(unprefixedKey as SideFilterAttr)
          ) {
            state.sideFilters[key] = value
              ?.split(',')
              .map(convertToNumberIfPossible);
          }
        }
      });
    },
    setIsReselectFlightBundleFetching: (state, action) => {
      state.isReselectFlightBundleFetching = action.payload;
    },
    updateModalSideFilters: (state, action) => {
      const { key, value } = action.payload;
      if (value === undefined) {
        if (
          state.modalSideFilters &&
          state.modalSideFilters.hasOwnProperty(key)
        ) {
          delete state.modalSideFilters[key];
        }
      } else {
        state.modalSideFilters[key] = value;
      }
    },
    setModalSideFilters: (state, action) => {
      state.modalSideFilters = action.payload;
    },
    updateSearchParams: (state, action) => {
      state.searchParams = action.payload;
    },
    updateDynamicPackageSearch: (state, action) => {
      state.searchParams.dynamicPackageSearch = action.payload;
      state.searchParams.isDpSearch = action.payload ? '1' : '0';
    },
    updateSearchResults: (state, action) => {
      state.searchResults = action.payload;
    },
    clearSearchParams: (state) => {
      state.searchParams = {
        ...initialState.searchParams,
        pax: null,
        rooms: null,
        vacationType: null,
        destination: null,
        adult: 0,
        child: 0,
        infant: 0,
      };
    },
    updateSearchInitialState: (
      state,
      action: {
        type: string;
        payload: {
          from: number;
          range: number;
          destination: string;
          destinationCity: string;
          extId?: string;
          departure: string;
          departureCity: string;
        };
      }
    ) => {
      const currParams = new URL(window.location.href).searchParams;
      let from, to, date, feature, mfeature;
      if (currParams.get('from')) {
        from = currParams.get('from');
      }
      if (currParams.get('date')) {
        date = currParams.get('date');
      }
      if (currParams.get('to') && from) {
        to = currParams.get('to');
      }
      if (!from) {
        from = moment().add(action.payload.from, 'd').format(searchDateFormat);
      }
      if (!date) {
        date = moment().add(action.payload.from, 'd').format(searchDateFormat);
      }
      if (!to) {
        to = moment()
          .add(action.payload.range + action.payload.from, 'd')
          .format(searchDateFormat);
      }
      if (currParams.get('feature')) {
        feature = currParams.get('feature');
      }
      if (currParams.get('mfeature')) {
        mfeature = currParams.get('mfeature');
      }

      if (!state.searchParams.from) {
        state.searchParams.from = from;
      }
      if (!state.searchParams.date) {
        state.searchParams.date = date;
      }
      if (!state.searchParams.to) {
        state.searchParams.to = to;
      }
      if (!state.searchParams.destination) {
        state.searchParams.destination = action.payload.destination;
      }
      if (!state.searchParams.departure) {
        state.searchParams.departure = action.payload.departure;
      }
      if (!state.searchParams.destinationCity) {
        state.searchParams.destinationCity = action.payload.destinationCity;
      }
      if (!state.searchParams.extId) {
        state.searchParams.extId = action.payload.extId;
      }
      if (state.searchParams.destinationCity === '') {
        state.searchParams.destinationCity = null;
      }
      if (state.searchParams.extId === '') {
        state.searchParams.extId = null;
      }
      if (!state.searchParams.departureCity) {
        state.searchParams.departureCity = action.payload.departureCity;
      }

      if (!state.searchParams.feature) {
        state.searchParams.feature = feature;
      }
      if (!state.searchParams.mfeature) {
        state.searchParams.mfeature = mfeature;
      }
    },
    updateSiteFilter: (state, action) => {
      state.sideFilters[action.payload.key] = action.payload.value;
    },
    setSiteFilters: (state, action) => {
      state.sideFilters = action.payload;
    },
    clearSideFilter: (state, action) => {
      const filterEntityType = action?.payload?.filterEntityType;
      const isReturnFlight = action?.payload?.isReturnFlight;

      if (
        filterEntityType === 'dynamic_flights' &&
        !isEmpty(state?.sideFilters)
      ) {
        let updatedFilters: Record<string, any> = {};
        for (const key in state?.sideFilters) {
          if (!key.startsWith(isReturnFlight ? 'f2_' : 'f1_')) {
            updatedFilters[key] = state.sideFilters[key];
          }
        }

        state.sideFilters = updatedFilters;
      } else if (
        state.searchParams.directFlight == '1' &&
        window.location.pathname.includes('flights')
      ) {
        state.sideFilters = {
          f1_stops: [0],
          f2_stops: [0],
          stops: [0],
        };
      } else {
        state.sideFilters = {};
      }
    },
    clearModalSideFilter: (state, action) => {
      const filterEntityType = action?.payload?.filterEntityType;
      const isReturnFlight = action?.payload?.isReturnFlight;

      if (
        filterEntityType === 'dynamic_flights' &&
        !isEmpty(state?.modalSideFilters)
      ) {
        let updatedFilters: Record<string, any> = {};
        for (const key in state?.modalSideFilters) {
          if (!key.startsWith(isReturnFlight ? 'f2_' : 'f1_')) {
            updatedFilters[key] = state.modalSideFilters[key];
          }
        }

        state.modalSideFilters = updatedFilters;
      } else if (
        state.modalSearchParams.directFlight == '1' &&
        window.location.pathname.includes('flights')
      ) {
        state.modalSideFilters = {
          f1_stops: [0],
          f2_stops: [0],
          stops: [0],
        };
      } else {
        state.modalSideFilters = {};
      }
    },
    updateHotelIdentifier: (state, action) => {
      state.searchParams.h = action.payload;
    },
    updateCheapestFlightIdentifier: (state, action) => {
      state.searchParams.f = action.payload;
    },
    setFlightDestinationAirport: (state, action) => {
      state.flightDestinationAirport = action.payload;
    },
    setFlightDepartureAirport: (state, action) => {
      state.flightDepartureAirport = action.payload;
    },
    // Set on explicit search-button clicks so SearchResults scrolls into view
    // once after navigation; consumed (cleared) by SearchResults.
    requestScrollToResults: (state) => {
      state.scrollToResults = true;
    },
    clearScrollToResults: (state) => {
      state.scrollToResults = false;
    },
  },
});

export function parseSearchToSearchParams(search: string) {
  const params = new URLSearchParams(search);
  const out: Record<string, any> = {
    // same keys as your initialState.searchParams with safe defaults
    f: null,
    h: null,
    dynamicPackageSearch: null,
    from: null,
    to: null,
    eventFrom: null,
    eventTo: null,
    pax: null,
    rooms: null,
    adult: 2,
    child: 0,
    infant: 0,
    oneWay: '0',
    isDpSearch: '0',
    prvmd: '0',
    vacationType: null,
    destination: null,
    departure: null,
    destinationCity: null,
    extId: null,
    departureCity: null,
    directFlight: '0',
    boardBasis: null,
    multi_room_guests: null,
    hotelStar: null,
    date: null,
    mfeature: null,
    feature: null,
    ext_type: null,
    extName: null,
  };

  params.forEach((value, key) => {
    // // reuse your convertToNumberIfPossible where applicable
    // if (!out.hasOwnProperty(key)) {
    //   // treat unknown keys as side-filters — leave them out of searchParams
    //   return;
    // }
    if (key === 'pax') {
      out.pax = value === '' || value === null ? null : value.split(',');
      return;
    }
    if (key === 'hotel_stars') {
      out.hotelStar = value === '' || value === null ? null : value.split(',');
      return;
    }
    if (key === 'multi_room_guests') {
      try {
        out.multi_room_guests = JSON.parse(value);
      } catch {
        // try decode + fallback
        try {
          out.multi_room_guests = JSON.parse(decodeURIComponent(value));
        } catch {
          out.multi_room_guests = out.multi_room_guests;
        }
      }
      return;
    }

    if (['boardBasis', 'hotelStar'].includes(key)) {
      out[key] = value === '' || value === null ? null : value.split(',');
      return;
    }

    // for numeric-ish values
    out[key] = convertToNumberIfPossible(value);
  });

  return out;
}

export const {
  updateModalSearchParams,
  setIsReselectFlightBundleFetching,
  updateModalSideFilters,
  setModalSideFilters,
  clearModalSideFilter,
  updateSearchParams,
  updateDynamicPackageSearch,
  updateSearchResults,
  updateSearchInitialState,
  clearSearchParams,
  updateSiteFilter,
  clearSideFilter,
  setFlightDestinationAirport,
  setFlightDepartureAirport,
  setSiteFilters,
  updateHotelIdentifier,
  updateCheapestFlightIdentifier,
  updateSideFiltersFunction,
  requestScrollToResults,
  clearScrollToResults,
} = searchSlice.actions;

export const selectSearchParamsPaxCount = (
  state: RootState,
  useMultiRoomGuests?: boolean
) => {
  const multiRoomGuests = state.search.searchParams.multi_room_guests;
  let totalPax: { adult: number; child: number; infant: number } | null;
  if (useMultiRoomGuests && multiRoomGuests && multiRoomGuests.length > 1) {
    totalPax = convertMultiRoomToPaxCount(multiRoomGuests);
  } else {
    totalPax = {
      adult: Number(state.search.searchParams.adult),
      child: Number(state.search.searchParams.child),
      infant: Number(state.search.searchParams.infant),
    };
  }

  return totalPax;
};

export default searchSlice.reducer;
